import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { IcBtn } from "../../../components/button/IcBtn";
import { HeaderCartBtn } from "../../../components/heading/HeaderCartBtn";
import { icAsset, icUserOutline } from "../../../components/image/image-src";
import { NoResultsWereFound } from "../../../components/not-found/NoResultsWereFound";
import { SvgSearch } from "../../../components/svg/SvgSearch";
import { useAuth } from "../../../context/AuthContext";
import { useMounted } from "../../../hooks/useMounted";
import { useTranslationEx } from "../../../hooks/useTranslationEx";
import { EnhancedI18n } from "../../../i18nEnhanced";
import ConfigService from "../../../services/configService";
import ProductService from "../../../services/productService";
import { getDefaultLanguage } from "../../../toStdLanguages";
import { useLangMenu } from "../../../utils/conf";
import Utils from "../../../utils/utils";
import { RecommendCities } from "./RecommendCity";
import { WideBanner } from "./WideBanner";
interface SearchHistory {
  keyword: string;
  date: string;
}

const SmHeaderIcons = () => {
  const { i18n } = useTranslation();
  const { t2: t } = useTranslationEx();
  const [currentLang, setCurrentLang] = useState(Utils.getCurrentLanguage());
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, setShowLoginModal } = useAuth();

  useEffect(() => {
    const storedLanguage = getDefaultLanguage();
    if (storedLanguage && storedLanguage !== i18n.language) {
      setCurrentLang(storedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".language-dropdown")) {
        setIsLangMenuOpen(false);
      }
    };

    if (isLangMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isLangMenuOpen]);

  const changeLanguage = (lng: string) => {
    setCurrentLang(lng);
    EnhancedI18n.changeLanguage(lng, true);
    setIsLangMenuOpen(false); // Close menu after selection
  };

  const languages = useLangMenu();
  const renderLangMenu = () => {
    if (languages.length <= 1) return null; // No need to show menu if only one language
    return (
      <div
        className={`absolute right-0 top-12 z-[1000] w-[12rem] overflow-hidden rounded-[0.9375rem] border border-[#EBEBEB] bg-white shadow-lg transition-all duration-200 ${
          isLangMenuOpen ? "block opacity-100" : "hidden opacity-0"
        }`}
      >
        <ul>
          {languages.map((lang, index) => (
            <li
              key={index}
              className={`h-12 cursor-pointer content-center hover:bg-primary`}
              onClick={() => changeLanguage(lang.icon)}
            >
              <div className="flex gap-3 p-3">
                <img
                  className="h-5 w-5 rounded-full border border-[#DEDEDE]"
                  src={icAsset(`../assets/icons/${lang.icon}.svg`)}
                  alt={lang.icon}
                />
                <p className="text-[0.875rem] font-normal leading-[1.1919rem]">
                  {lang.label}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="absolute right-0 top-0 z-20 !mt-0 flex items-center justify-between px-[1rem] py-[0.875rem] lg:hidden">
      <HeaderCartBtn vari="BASE" />
      <div className="language-dropdown relative">
        <button
          className="flex size-10 items-center justify-center"
          onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
        >
          <img
            className="size-6"
            src={icAsset(`../assets/icons/${currentLang}.svg`)}
            alt={currentLang}
          />
        </button>
        {renderLangMenu()}
      </div>
      <IcBtn
        onClick={() =>
          isAuthenticated
            ? navigate("/mypage/profile")
            : setShowLoginModal(true)
        }
        ic={icUserOutline}
      />
    </div>
  );
};

const FindYourNextPlan: React.FC<{
  banners: BannerDTO[];
}> = ({ banners }) => {
  const { t2: t } = useTranslationEx();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<
    {
      keyword?: string;
      city?: CityDTO;
      product?: ProductTinyResponse;
    }[]
  >([]);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const { data: recommendData, isLoading } = useQuery<
    RecommendResponseDTO | undefined
  >(["recommendProducts", searchTerm], async () => {
    if (!searchTerm) return undefined;
    const result = await ProductService.getRecommend(5, 1, searchTerm);
    return result;
  });
  const { data: bannerBigTitle } = useQuery<ConfigResponse>(
    ["bannerBigTitle"],
    async () => {
      const result = await ConfigService.getConfigByKey("banner_big_title");
      return result || "";
    },
  );

  const { data: bannerSmallTitle } = useQuery<ConfigResponse>(
    ["bannerSmallTitle"],
    async () => {
      const result = await ConfigService.getConfigByKey("banner_small_title");
      return result || "";
    },
  );

  useEffect(() => {
    const history = localStorage.getItem("searchHistory");
    if (history) {
      setSearchHistory(JSON.parse(history));
    }
  }, []);

  useEffect(() => {
    // Filter search history based on search term
    if (searchTerm || recommendData) {
      const updatedHistory = [...searchHistory]
        .filter(
          (term) =>
            term.date >
            new Date(
              new Date().setDate(new Date().getDate() - 30),
            ).toISOString(),
        )
        .map((term) => term.keyword);
      if (searchTerm === "") {
        setFilteredHistory(
          updatedHistory.slice(0, 10).map((keyword) => ({ keyword })),
        );
        return;
      }
      setFilteredHistory([
        ...(recommendData?.cities ?? []).map((city) => ({ city })),
        ...(recommendData?.products ?? []).map((product) => ({ product })),
      ]);
    } else {
      setFilteredHistory([]);
    }
  }, [searchTerm, searchHistory, recommendData]);

  const handleSearch = () => {
    saveSearchHistory(searchTerm);
    navigate(`/home/search?keyword=${encodeURIComponent(searchTerm.trim())}`);
  };

  const saveSearchHistory = (keyword: string) => {
    if (keyword.trim() && !searchHistory.some((s) => s.keyword === keyword)) {
      const updatedHistory = [
        { date: new Date().toISOString(), keyword: keyword },
        ...searchHistory,
      ].slice(0, 10);
      setSearchHistory(updatedHistory);
      localStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
    }
  };
  const renderEmptyState = () => {
    return searchTerm !== "" &&
      !recommendData?.products?.length &&
      !recommendData?.cities?.length &&
      !isLoading ? (
      <li>
        <NoResultsWereFound />
      </li>
    ) : null;
  };
  const handleSelectSuggestion = (suggestion: string) => {
    setSearchTerm(suggestion);
    setFilteredHistory([]);
    saveSearchHistory(suggestion);
    navigate(`/home/search?keyword=${encodeURIComponent(suggestion.trim())}`);
    // Perform search action here
  };
  const createMarkup = (text: string) => {
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const newText = text.replace(regex, "<b>$1</b>");
    return { __html: newText };
  };
  const createMarkup2 = (text: string) => {
    const regex = new RegExp(`(${searchTerm})`, "gi");
    const newText = text.replace(regex, "<b>$1</b>");
    return {
      __html: `<img class="inline mr-2 size-5" src=${icAsset("../../../assets/images/icons/gps.svg")} alt="Shopping Cart" loading="eager"><span>${newText}</span>`,
    };
  };
  const { mounted } = useMounted();
  return (
    <WideBanner banners={banners}>
      <SmHeaderIcons />
      <div className="pointer-events-none absolute z-10 flex w-full max-w-[50rem] flex-col items-center justify-center gap-[20px] px-4 sm:px-0">
        <div className="flex flex-col items-center gap-[0.875rem]">
          <h1 className="text-[2rem] font-semibold leading-none text-white sm:text-[3.125rem]">
            {/* Get  value of bannerBigTitle.value if not have ""*/}
            {mounted && t(bannerBigTitle?.value || "")}
          </h1>
          <p className="px-[1.375rem] text-center text-sm font-normal text-white sm:px-0 sm:text-[1.125rem]">
            {mounted && t(bannerSmallTitle?.value || "")}
          </p>
        </div>
        {/* dummy placeholder for spacing */}
        <div className="h-[38px] w-full" />
      </div>

      {/* dummy margin for spacing */}
      <div className="absolute z-10 !mt-[105px] flex w-full max-w-[50rem] flex-col items-center justify-center gap-[20px] px-4 sm:px-0">
        <div className="relative w-full max-w-[50rem]">
          <input
            type="text"
            style={{ boxShadow: "0px 0px 4px 2px #00000026 inset" }}
            className="sm:text-text-lg hidden h-full w-full rounded-full border border-[#DCDCDC] py-2 pl-[3.5rem] pr-2 text-sm font-medium text-[#BFC5C9] outline-none transition-all focus:rounded-bl-none focus:rounded-br-none focus:rounded-tl-[1rem] focus:rounded-tr-[1rem] focus:border-primary sm:leading-[1.75rem]"
            placeholder={t("What are you finding?")}
            value={searchTerm}
            onFocus={() => setIsFocused(true)}
            onBlurCapture={() => {
              setTimeout(() => {
                setIsFocused(false);
              }, 100);
            }}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="pointer-events-none absolute inset-y-0 !hidden items-center pl-4 sm:flex">
            <SvgSearch size="1.5rem" />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            className="absolute inset-y-0 right-[0.5rem] top-[0.5rem] !hidden h-10 w-[6.3125rem] items-center justify-center rounded-[6.1875rem] bg-[#A8C6FF] leading-none text-[#232323] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex"
          >
            {t("Search")}
          </button>
          <button
            type="button"
            onClick={handleSearch}
            className="absolute inset-y-0 right-[1.125rem] flex !hidden items-center sm:hidden"
          >
            <SvgSearch size="1.25rem" />
          </button>
          {/* {isFocused && ( */}
          {/* <ul
            style={{ boxShadow: "0px 4px 12px 0px #0000000D" }}
            className={`z-10 transform py-8 transition-all duration-300 ${
              isFocused ? "scale-100 opacity-100" : "-z-10 scale-95 opacity-0"
            } absolute max-h-60 w-full overflow-y-auto rounded-2xl rounded-tl-none rounded-tr-none bg-white shadow-lg`}
          >
            {searchTerm === "" && (
              <p className="px-[1.125rem] pb-2 text-base font-semibold leading-[1.25rem] text-[#232323]">
                {t("Recent history")}
              </p>
            )}
            {filteredHistory.map((suggestion, index) => {
              const txt =
                suggestion.city?.name ||
                suggestion.product?.name ||
                suggestion.keyword ||
                "";
              return (
                <li
                  key={index}
                  className="cursor-pointer px-[1.125rem] py-2 hover:bg-gray-200"
                  onClick={() => handleSelectSuggestion(txt)}
                  dangerouslySetInnerHTML={
                    suggestion.city?.name
                      ? createMarkup2(txt)
                      : createMarkup(txt)
                  }
                ></li>
              );
            })}
            {renderEmptyState()}
          </ul> */}
          {/* )} */}
          <div className={isFocused ? "hidden" : ""}>
            <RecommendCities />
          </div>
        </div>
      </div>
    </WideBanner>
  );
};
export default FindYourNextPlan;
